/**
 * 채널 게시물 작성 모달 — 글 / 투표 두 탭.
 *
 * FormModal 표준 사용. 작성자 정보는 본문 상단에 배치, 파일 첨부 버튼은 footerExtras 로.
 */
import React, { useEffect, useRef, useState } from "react";
import {
    Avatar, Box, Button, IconButton, Stack, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import CloseRoundedIcon       from "@mui/icons-material/CloseRounded";
import AttachFileRoundedIcon  from "@mui/icons-material/AttachFileRounded";
import BallotRoundedIcon      from "@mui/icons-material/BallotRounded";
import AddRoundedIcon         from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon      from "@mui/icons-material/DeleteRounded";

import RichEditor from "../../../component/common/RichTextEditor";
import FormField  from "../../../component/common/FormField";
import FormModal  from "../../../component/common/FormModal";
import { channelApi } from "../api/channelApi";
import { notify }     from "../../../util/toast";
import { runApiAction, getApiErrorMessage } from "../../../util/apiFeedback";
import { extractMentionedEmployeeCodes, withoutSelf } from "../utils/mentions";
import ActionButton from "../../../component/common/ActionButton";
import ComboBox from "../../../component/common/ComboBox";

function stripHtml(html) {
    if (!html) return "";
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function PendingFileChip({ file, onRemove }) {
    return (
        <Box sx={{
            display: "inline-flex", alignItems: "center", gap: 0.6, maxWidth: 200,
            px: 1, py: 0.4, fontSize: 12, bgcolor: "#f1f5f9", border: "1px solid #e2e8f0",
            borderRadius: 1.2,
        }}>
            <Typography noWrap sx={{ fontSize: 12 }}>{file.name}</Typography>
            <IconButton size="small" onClick={onRemove} sx={{ p: 0.2 }}>
                <CloseRoundedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
            </IconButton>
        </Box>
    );
}

function ComposeTabBtn({ label, icon, active, onClick }) {
    return (
        <ActionButton
            variant={active ? "primary" : "secondary"}
            startIcon={icon ?? false}
            onClick={onClick}
            sx={{
                px: 2, py: 0.6, fontSize: 13, fontWeight: 600, gap: 0.5,
                ...(active
                    ? {}
                    : { color: "#64748b", borderColor: "transparent",
                        "&:hover": { borderColor: "transparent", bgcolor: "#f1f5f9", color: "#64748b" } }),
                transition: "all 0.15s",
            }}
            label={label}
        />
    );
}

function PollSettingRow({ checked, onChange, label, desc }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5 }}>
            <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{label}</Typography>
                <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{desc}</Typography>
            </Box>
            <Switch
                checked={checked} onChange={e => onChange(e.target.checked)} size="small"
                sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#2563eb" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#2563eb" },
                }}
            />
        </Box>
    );
}

export default function FeedComposeModal({
    open, onClose, channelId, myInfo, members = [], onPosted,
    /** 이 채널의 폴더 목록 — 비어 있으면 폴더 선택 자체를 그리지 않는다. */
    folders = [],
    /** 지금 보고 있는 폴더 — 기본값으로 채운다. '전체'·'미분류' 를 보고 있으면 null. */
    defaultFolderId = null,
}) {
    /* ── 글 탭 상태 ─ */
    const [composeTab, setComposeTab] = useState("text");  // "text" | "poll"
    const [title, setTitle]           = useState("");
    const [text, setText]             = useState("");
    const [files, setFiles]           = useState([]);
    const [editorKey, setEditorKey]   = useState(0);
    const fileInputRef                = useRef();

    /**
     * 등록할 폴더. 열 때마다 <b>보고 있던 폴더</b>로 초기화된다 —
     * 이 기본값 하나가 "엉뚱한 폴더에 글 쓰기" 를 실질적으로 막는다(폴더를 고르는 걸 잊어도
     * 지금 보고 있는 파트에 들어간다).
     */
    const [folderId, setFolderId] = useState(defaultFolderId);
    useEffect(() => { if (open) setFolderId(defaultFolderId); }, [open, defaultFolderId]);

    // 게시·업로드 진행 중 — FormModal 의 saving 으로 전달되어 자동 backdrop + LoadingDots 표시.
    // 첨부 파일 다수일 때 S3 업로드가 10초 이상 걸리는 경우 사용자 피드백.
    const [posting, setPosting] = useState(false);

    /* ── 투표 탭 상태 ─ */
    const [pollQuestion, setPollQuestion]           = useState("");
    const [pollOptions, setPollOptions]             = useState(["", ""]);
    const [pollAnonymous, setPollAnonymous]         = useState(false);
    const [pollMultiChoice, setPollMultiChoice]     = useState(false);
    const [pollPrivateResult, setPollPrivateResult] = useState(false);
    const [pollDeadline, setPollDeadline]           = useState("");

    const resetAll = () => {
        setComposeTab("text");
        setTitle(""); setText(""); setFiles([]); setEditorKey(k => k + 1);
        setFolderId(defaultFolderId);
        setPollQuestion(""); setPollOptions(["", ""]);
        setPollAnonymous(false); setPollMultiChoice(false);
        setPollPrivateResult(false); setPollDeadline("");
    };

    const handleClose = () => { onClose?.(); resetAll(); };

    const handleUploadEditorImage = async (file) => {
        try {
            const res = await channelApi.uploadFile(channelId, file, null, null, null);
            return res.fileUrl;
        } catch (err) {
            notify(getApiErrorMessage(err, "에디터 이미지 업로드 실패"), "error");
            return null;
        }
    };

    const handlePost = async () => {
        const plainText = stripHtml(text);
        if (!plainText.trim() && !files.length) return;
        if (posting) return; // 중복 클릭 방지
        setPosting(true);
        try {
            const feedRes = await channelApi.createFeed(channelId, {
                title: title.trim() || null,
                content: text || "<p></p>",
                mentionedEmployeeCodes: extractMentionedEmployeeCodes(text, members),
                folderId: folderId ?? null,
            });
            const feedId = feedRes.id ?? feedRes.data?.id;
            for (const f of files) {
                await channelApi.uploadFile(channelId, f, feedId, null);
            }
            handleClose();
            onPosted?.();
        } catch (err) {
            notify(getApiErrorMessage(err, "피드 작성 실패"), "error");
        } finally {
            setPosting(false);
        }
    };

    const handlePostPoll = () => {
        if (!pollQuestion.trim()) { notify("투표 질문을 입력하세요.", "warning"); return; }
        const validOptions = pollOptions.map(o => o.trim()).filter(Boolean);
        if (validOptions.length < 2) { notify("선택지를 최소 2개 입력하세요.", "warning"); return; }
        return runApiAction({
            request: () => channelApi.createPoll(channelId, {
                question:     pollQuestion.trim(),
                options:      validOptions,
                folderId:     folderId ?? null,
                anonymous:    pollAnonymous,
                multiChoice:  pollMultiChoice,
                privateResult: pollPrivateResult,
                deadline:     pollDeadline ? new Date(pollDeadline).toISOString() : null,
            }),
            errorMsg: "투표 생성 실패",
            onSuccess: () => { handleClose(); onPosted?.(); },
        });
    };

    const updatePollOption = (idx, val) =>
        setPollOptions(prev => prev.map((o, i) => i === idx ? val : o));
    const addPollOption    = () => setPollOptions(prev => [...prev, ""]);
    const removePollOption = (idx) =>
        setPollOptions(prev => prev.length <= 2 ? prev : prev.filter((_, i) => i !== idx));

    const isText = composeTab === "text";
    const canPostText = stripHtml(text).trim() || files.length > 0;
    const canPostPoll = pollQuestion.trim() && pollOptions.filter(o => o.trim()).length >= 2;

    return (
        <FormModal
            open={open}
            onClose={handleClose}
            title={isText ? "새 글 작성" : "새 투표 작성"}
            subtitle={`${myInfo?.name ?? ""}${myInfo?.positionName ? ` · ${myInfo.positionName}` : ""}${myInfo?.deptName ? ` · ${myInfo.deptName}` : ""}`}
            maxWidth="md"
            onSave={isText ? handlePost : handlePostPoll}
            saveLabel={isText ? "등록" : "투표 등록"}
            saveDisabled={isText ? !canPostText : !canPostPoll}
            saving={posting}
            footerExtras={
                isText ? (
                    <>
                        {/* 아이콘만 두면 클립이 무슨 기능인지 눌러 보기 전엔 알 수 없다 — 글자를 함께 둔다. */}
                        <ActionButton variant="secondary"
                            startIcon={<AttachFileRoundedIcon sx={{ fontSize: 17 }} />}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ fontSize: 12.5, color: "#475569" }}
                            label={files.length > 0 ? `첨부파일 ${files.length}` : "첨부파일"} />
                        <input ref={fileInputRef} type="file" hidden multiple
                            onChange={e => setFiles(p => [...p, ...Array.from(e.target.files)])} />
                    </>
                ) : null
            }
        >
            {/* 작성자 + 탭 */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                <Avatar sx={{ width: 32, height: 32, fontSize: 13 }}>
                    {myInfo?.name?.[0] ?? "나"}
                </Avatar>
                <Box sx={{ flex: 1 }} />
                <ComposeTabBtn label="글"   active={isText} onClick={() => setComposeTab("text")} />
                <ComposeTabBtn label="투표" icon={<BallotRoundedIcon sx={{ fontSize: 15 }} />}
                    active={!isText} onClick={() => setComposeTab("poll")} />
            </Stack>

            {/* 폴더 선택 — 폴더를 쓰는 채널에서만 나온다. 글·투표 두 탭에 공통. */}
            {folders.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                    <FormField label="폴더">
                        <ComboBox
                            options={[
                                { value: "", label: "미분류 (폴더 없음)" },
                                ...folders.map(f => ({ value: f.id, label: f.name })),
                            ]}
                            value={folderId ?? ""}
                            onChange={v => setFolderId(v === "" ? null : Number(v))}
                            clearable={false}
                        />
                    </FormField>
                </Box>
            )}

            {isText ? (
                <Stack spacing={1.5}>
                    <FormField label="제목">
                        <TextField
                            fullWidth size="small" variant="outlined"
                            placeholder="제목을 입력하세요"
                            value={title} onChange={e => setTitle(e.target.value)}
                        />
                    </FormField>
                    <Box>
                        <RichEditor
                            key={editorKey}
                            initialValue={text}
                            onChange={setText}
                            placeholder="무슨 생각을 하고 계신가요? (@로 동료 멘션)"
                            minHeight={320}
                            onUploadImage={handleUploadEditorImage}
                            mentionMembers={withoutSelf(members, myInfo?.id)}
                        />
                    </Box>
                    {files.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {files.map((f, i) => (
                                <PendingFileChip
                                    key={i} file={f}
                                    onRemove={() => setFiles(files.filter((_, j) => j !== i))}
                                />
                            ))}
                        </Stack>
                    )}
                </Stack>
            ) : (
                <Stack spacing={2}>
                    <FormField label="질문">
                        <TextField
                            fullWidth size="small" variant="outlined"
                            placeholder="무엇을 물어볼까요?"
                            value={pollQuestion}
                            onChange={e => setPollQuestion(e.target.value)}
                        />
                    </FormField>

                    <FormField label={`선택지 (${pollOptions.length})`}>
                        <Stack spacing={0.8}>
                            {pollOptions.map((opt, idx) => (
                                <Stack key={idx} direction="row" spacing={0.8} alignItems="center">
                                    <TextField
                                        fullWidth size="small" variant="outlined"
                                        placeholder={`선택지 ${idx + 1}`}
                                        value={opt}
                                        onChange={e => updatePollOption(idx, e.target.value)}
                                    />
                                    <IconButton size="small"
                                        disabled={pollOptions.length <= 2}
                                        onClick={() => removePollOption(idx)}
                                        sx={{ p: 0.6 }}>
                                        <DeleteRoundedIcon sx={{ fontSize: 18, color: pollOptions.length <= 2 ? "#cbd5e1" : "#ef4444" }} />
                                    </IconButton>
                                </Stack>
                            ))}
                            <ActionButton variant="secondary" startIcon={<AddRoundedIcon />}
                                onClick={addPollOption}
                                sx={{
                                    color: "#2563eb", borderColor: "transparent",
                                    alignSelf: "flex-start",
                                    "&:hover": { borderColor: "transparent", bgcolor: "#eff6ff", color: "#2563eb" },
                                }}
                                label="선택지 추가" />
                        </Stack>
                    </FormField>

                    <FormField label="마감일 (선택)">
                        <TextField
                            fullWidth size="small" type="datetime-local"
                            value={pollDeadline}
                            onChange={e => setPollDeadline(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </FormField>

                    <FormField label="옵션">
                        <Stack divider={<Box sx={{ borderTop: "1px solid #f1f5f9" }} />}>
                            <PollSettingRow
                                checked={pollMultiChoice}   onChange={setPollMultiChoice}
                                label="복수 선택 허용"      desc="여러 항목을 동시에 선택할 수 있게 합니다." />
                            <PollSettingRow
                                checked={pollAnonymous}     onChange={setPollAnonymous}
                                label="익명 투표"            desc="누가 어떤 항목에 투표했는지 표시되지 않습니다." />
                            <PollSettingRow
                                checked={pollPrivateResult} onChange={setPollPrivateResult}
                                label="결과 비공개"          desc="투표 종료 전에는 본인 외 결과를 볼 수 없습니다." />
                        </Stack>
                    </FormField>
                </Stack>
            )}
        </FormModal>
    );
}
